import React, { useState } from 'react';
import { Badge, Button } from '../../components/UI';
import CostBox from './CostBox';
import EditPanel from './EditPanel';
import { SKIP_REASONS, OUTREACH_STATUS_LABEL, isOpen } from './marketingFormat';
import styles from './Marketing.module.css';

const OUTREACH_EDIT_FIELDS = [
  { key: 'subject', label: 'Subject' },
  { key: 'body', label: 'Email', multiline: true },
  { key: 'subject_en', label: 'Subject (English translation)' },
  { key: 'body_en', label: 'Email (English translation)', multiline: true },
];

const Row = ({ label, children }) => (
  <div className={styles.field}>
    <div className={styles.fieldLabel}>{label}</div>
    <div className={styles.fieldValue}>{children}</div>
  </div>
);

const OutreachCard = ({ item, monthSpentUsd, settings, busy, onApprove, onSkip, onEdit, onNeverContact, onMark, onRetry }) => {
  const [english, setEnglish] = useState(false);
  const [editing, setEditing] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const d = item.draft || {};
  const t = item.target || {};
  const open = isOpen(item);
  // An approved email can still be un-queued and a failed one dropped
  // (queue.js: approved → skipped, failed → skipped). Blocking the business
  // works whatever the item's state — also after a sent email, e.g. when the
  // business replies "please don't email us"; that never changes the sent item.
  const canSkip = open || ['approved', 'failed'].includes(item.status);
  const canNeverContact = open || ['approved', 'failed', 'sent'].includes(item.status);

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.cardType}>
          OUTREACH · {item.kind === 'followup' ? 'follow-up · ' : ''}{t.name}
        </span>
        <span>
          {item.warnings?.length > 0 && <Badge variant="warning">⚑ {item.warnings.length} warning{item.warnings.length > 1 ? 's' : ''}</Badge>}{' '}
          {item.dryRun && <Badge variant="warning">dry run</Badge>}{' '}
          {!open && <Badge variant={item.status === 'failed' ? 'danger' : 'secondary'}>{OUTREACH_STATUS_LABEL[item.status] || item.status}</Badge>}
        </span>
      </div>

      {item.problems?.length > 0 && (
        <ul className={styles.problems}>{item.problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
      )}
      {item.sendError && <p className={styles.banner}>Send failed: {item.sendError}</p>}

      <Row label="To">{t.email}{item.sentTo && item.sentTo !== t.email ? ` (sent to ${item.sentTo})` : ''}</Row>
      <Row label="Found on">{t.emailSourceUrl ? <a href={t.emailSourceUrl} target="_blank" rel="noreferrer">{t.emailSourceUrl}</a> : '—'}</Row>
      <Row label="Business">{[t.address, t.rating != null ? `${t.rating}★ (${t.ratingCount ?? 0})` : null, t.category].filter(Boolean).join(' · ')}</Row>

      {editing ? (
        <EditPanel fields={OUTREACH_EDIT_FIELDS} draft={d} busy={busy}
          onCancel={() => setEditing(false)}
          onSave={async (fields) => { if (await onEdit(item, fields)) setEditing(false); }} />
      ) : item.draft && (
        <>
          <div className={styles.langToggle}>
            <button type="button" className={!english ? styles.langActive : ''} onClick={() => setEnglish(false)}>{(t.language || 'nl').toUpperCase()}</button>
            <button type="button" className={english ? styles.langActive : ''} onClick={() => setEnglish(true)}>EN</button>
          </div>
          <Row label="Subject">{english ? d.subject_en : d.subject}</Row>
          <Row label="Email">{english ? d.body_en : d.body}</Row>
          <Row label="Why this business">{d.why_this_target}</Row>
          <Row label="If you walk in">{d.in_person_opener}</Row>
        </>
      )}

      {item.warnings?.length > 0 && (
        <ul className={styles.warnings}>{item.warnings.map((w, i) => <li key={i}><strong>{w.rule}</strong> — {w.note}</li>)}</ul>
      )}

      <div className={styles.cardFooter}>
        <CostBox cost={item.cost} monthSpentUsd={monthSpentUsd} settings={settings} />
        <div className={styles.actions}>
          {open && !editing && (
            <>
              <Button onClick={() => onApprove(item)} disabled={item.status === 'flagged' || busy} loading={busy}>Approve &amp; queue</Button>
              {item.draft && <Button variant="outline" onClick={() => setEditing(true)} disabled={busy}>Edit</Button>}
            </>
          )}
          {canSkip && !editing && (
            <>
              {!skipping && <Button variant="outline" onClick={() => setSkipping(true)} disabled={busy}>Skip</Button>}
              {skipping && SKIP_REASONS.map((r) => (
                <Button key={r} size="sm" variant="ghost" disabled={busy} onClick={() => onSkip(item, r)}>{r}</Button>
              ))}
            </>
          )}
          {canNeverContact && !editing && (
            <Button variant="danger" size="sm" disabled={busy}
              onClick={() => { if (window.confirm(`Never contact ${t.name} again?`)) onNeverContact(item); }}>
              Never contact
            </Button>
          )}
          {item.status === 'failed' && !item.deliveryUnknown && <Button onClick={() => onRetry(item)} disabled={busy}>Retry send</Button>}
          {item.status === 'failed' && item.deliveryUnknown && (
            <p className={styles.banner}>This email may already have been delivered. Check the Resend dashboard before contacting this business again; skip it here.</p>
          )}
          {item.sentUnrecorded && <p className={styles.banner}>Sent, but the send could not be recorded on the business. Check the Cloud Functions logs.</p>}
          {item.status === 'sent' && !item.dryRun && (
            <>
              <Button size="sm" variant="outline" onClick={() => onMark(item, 'replied')} disabled={busy}>Mark replied</Button>
              <Button size="sm" variant="outline" onClick={() => onMark(item, 'interested')} disabled={busy}>Mark interested</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OutreachCard;
